import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const DrugInformationForm = ({ drugData, onDrugDataChange, errors = {} }) => {
  const [showBatchDetails, setShowBatchDetails] = useState(false);

  const routeOptions = [
    { value: 'oral', label: 'Oral' },
    { value: 'intravenous', label: 'Intravenous (IV)' },
    { value: 'intramuscular', label: 'Intramuscular (IM)' },
    { value: 'subcutaneous', label: 'Subcutaneous (SC)' },
    { value: 'topical', label: 'Topical' },
    { value: 'inhalation', label: 'Inhalation' },
    { value: 'rectal', label: 'Rectal' },
    { value: 'transdermal', label: 'Transdermal' },
    { value: 'other', label: 'Other' }
  ];

  const indicationOptions = [
    { value: 'hypertension', label: 'Hypertension' },
    { value: 'diabetes', label: 'Diabetes Mellitus' },
    { value: 'infection', label: 'Bacterial Infection' },
    { value: 'pain', label: 'Pain Management' },
    { value: 'depression', label: 'Depression' },
    { value: 'anxiety', label: 'Anxiety Disorder' },
    { value: 'cardiovascular', label: 'Cardiovascular Disease' },
    { value: 'respiratory', label: 'Respiratory Condition' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    onDrugDataChange({
      ...drugData,
      [field]: value
    });
  };

  const handleDrugSearch = () => {
    // Simulate drug database search
    console.log('Searching drug database for:', drugData?.drugName);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
          <Icon name="Pill" size={20} className="text-warning" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Drug Information</h3>
          <p className="text-sm text-muted-foreground">Suspect drug details and administration information</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Drug Name and Search */}
        <div className="flex space-x-3">
          <div className="flex-1">
            <Input
              label="Drug Name (Generic/Brand)"
              type="text"
              placeholder="Enter drug name"
              value={drugData?.drugName || ''}
              onChange={(e) => handleInputChange('drugName', e?.target?.value)}
              error={errors?.drugName}
              required
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={handleDrugSearch}
              iconName="Search"
              iconPosition="left"
              className="mb-1"
            >
              Search DB
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Active Ingredient */}
          <Input
            label="Active Ingredient"
            type="text"
            placeholder="Primary active ingredient"
            value={drugData?.activeIngredient || ''}
            onChange={(e) => handleInputChange('activeIngredient', e?.target?.value)}
            error={errors?.activeIngredient}
            required
          />

          {/* Strength/Dose */}
          <Input
            label="Strength/Dose"
            type="text"
            placeholder="e.g., 500mg, 10mg/ml"
            value={drugData?.strength || ''}
            onChange={(e) => handleInputChange('strength', e?.target?.value)}
            error={errors?.strength}
            required
          />

          {/* Route of Administration */}
          <Select
            label="Route of Administration"
            options={routeOptions}
            value={drugData?.route || ''}
            onChange={(value) => handleInputChange('route', value)}
            error={errors?.route}
            required
            placeholder="Select administration route"
          />

          {/* Indication */}
          <Select
            label="Indication for Use"
            options={indicationOptions}
            value={drugData?.indication || ''}
            onChange={(value) => handleInputChange('indication', value)}
            error={errors?.indication}
            searchable
            placeholder="Select or search indication"
          />

          {/* Start Date */}
          <Input
            label="Treatment Start Date"
            type="date"
            value={drugData?.startDate || ''}
            onChange={(e) => handleInputChange('startDate', e?.target?.value)}
            error={errors?.startDate}
            required
          />

          {/* End Date */}
          <Input
            label="Treatment End Date"
            type="date"
            value={drugData?.endDate || ''}
            onChange={(e) => handleInputChange('endDate', e?.target?.value)}
            error={errors?.endDate}
            description="Leave blank if ongoing"
          />

          {/* Daily Dose */}
          <Input
            label="Daily Dose"
            type="text"
            placeholder="e.g., 500mg twice daily"
            value={drugData?.dailyDose || ''}
            onChange={(e) => handleInputChange('dailyDose', e?.target?.value)}
            error={errors?.dailyDose}
          />

          {/* Duration */}
          <Input
            label="Treatment Duration"
            type="text"
            placeholder="e.g., 7 days, 2 weeks"
            value={drugData?.duration || ''}
            onChange={(e) => handleInputChange('duration', e?.target?.value)}
            error={errors?.duration}
          />
        </div>

        {/* Batch Information Toggle */}
        <div className="border-t border-border pt-4">
          <button
            type="button"
            onClick={() => setShowBatchDetails(!showBatchDetails)}
            className="flex items-center space-x-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-150"
          >
            <Icon 
              name={showBatchDetails ? 'ChevronDown' : 'ChevronRight'} 
              size={16} 
            />
            <span>Batch & Manufacturing Details</span>
          </button>
        </div>

        {/* Batch Details */}
        {showBatchDetails && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-muted/30 p-4 rounded-lg">
            <Input
              label="Batch/Lot Number"
              type="text"
              placeholder="Manufacturing batch number"
              value={drugData?.batchNumber || ''}
              onChange={(e) => handleInputChange('batchNumber', e?.target?.value)}
              error={errors?.batchNumber}
            />

            <Input
              label="Expiry Date"
              type="date"
              value={drugData?.expiryDate || ''}
              onChange={(e) => handleInputChange('expiryDate', e?.target?.value)}
              error={errors?.expiryDate}
            />

            <Input
              label="Manufacturer"
              type="text"
              placeholder="Manufacturing company"
              value={drugData?.manufacturer || ''}
              onChange={(e) => handleInputChange('manufacturer', e?.target?.value)}
              error={errors?.manufacturer}
            />

            <Input
              label="Marketing Authorization Holder"
              type="text"
              placeholder="MAH company name"
              value={drugData?.mahHolder || ''}
              onChange={(e) => handleInputChange('mahHolder', e?.target?.value)}
              error={errors?.mahHolder}
            />
          </div>
        )}

        {/* Drug Interaction Warning */}
        {drugData?.drugName && (
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Drug Interaction Check</h4>
                <p className="text-sm text-muted-foreground">
                  Checking for known interactions with concomitant medications...
                </p>
                <div className="mt-2">
                  <span className="text-xs px-2 py-1 bg-success/10 text-success rounded-full">
                    No critical interactions found
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrugInformationForm;